/**
 * Is this run finished, still being worked on, or cut off? (Deivydas 2026-10-21)
 *
 * A run has two stages in one job: intake (the account read) marks it
 * "drafted", and the agent stage then writes the draft — 15 seconds to two
 * minutes later. Opened in between, the ticket read "drafted · Nothing
 * drafted" (#1944819, #1944820). And a run whose job was killed mid-draft by
 * a deploy or a restart stays exactly like that for good; its job runs again
 * as a new run.
 *
 * The agent stage always leaves its mark on the facts (`router`, `agent` or
 * `disposition`), so a drafted run with no draft and none of those has not
 * been through it — yet, or ever.
 *
 * A run waiting out a model outage (Deivydas 2026-10-28) has no mark either:
 * the router could not reach its model, so the worker tries again later.
 * Its facts carry `modelRetry` with the next try, and it reads "waiting"
 * until a little past that time.
 */

export type RunStage = 'done' | 'working' | 'waiting' | 'interrupted';

/** Longer than any agent stage plus a wait behind the ticket's lock. */
export const STILL_WORKING_MS = 10 * 60_000;

const AGENT_MARKS = ['router', 'agent', 'disposition', 'automatedMessage', 'sms'] as const;

export function runStage(
  run: {
    status: string;
    escalated: boolean;
    draftReply: string | null;
    facts: unknown;
    startedAt?: string | null;
  },
  now: number = Date.now(),
): RunStage {
  if (run.status !== 'drafted' || run.escalated || run.draftReply) return 'done';
  const facts = typeof run.facts === 'object' && run.facts !== null ? (run.facts as Record<string, unknown>) : {};
  if (AGENT_MARKS.some((mark) => facts[mark] !== undefined)) return 'done';
  const wait = modelRetryOf(facts);
  if (wait && now < Date.parse(wait.nextAt) + STILL_WORKING_MS) return 'waiting';
  const started = run.startedAt ? Date.parse(run.startedAt) : Number.NaN;
  if (Number.isFinite(started) && now - started < STILL_WORKING_MS) return 'working';
  return 'interrupted';
}

/** The next try of a run waiting out a model outage, unless a newer run took over. */
export function modelRetryOf(facts: unknown): { attempt: number; of: number; nextAt: string } | null {
  const wait =
    typeof facts === 'object' && facts !== null ? (facts as Record<string, unknown>).modelRetry : undefined;
  if (typeof wait !== 'object' || wait === null) return null;
  const { attempt, of, nextAt, superseded } = wait as Record<string, unknown>;
  if (superseded === true || typeof attempt !== 'number' || typeof of !== 'number' || typeof nextAt !== 'string') {
    return null;
  }
  return Number.isFinite(Date.parse(nextAt)) ? { attempt, of, nextAt } : null;
}
